import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import { ErrorBox } from "../components/States";
import { useBackButton } from "../components/telegram";
import { fmtMoney, fmtMoneyInput, parseMoneyInput } from "../format";
import { MeResponse } from "../types";

// ─────────────────── Payment legs types ───────────────────

interface AMLItem {
  name: string;
  included: boolean;
  optional: boolean;
}

interface DealPayment {
  id: number;
  deal_id: number;
  payment_type: string;
  direction: "receivable" | "payable" | null;
  amount: number | null;
  amount_currency: string | null;
  status: "review" | "sent" | "aml_package_required" | "received" | "returned" | "cancelled";
  currency: string;
  from_counterparty_id: number | null;
  from_bank_name: string | null;
  from_account_no: string | null;
  from_swift: string | null;
  from_bic: string | null;
  to_counterparty_id: number | null;
  to_bank_name: string | null;
  to_account_no: string | null;
  to_swift: string | null;
  to_bic: string | null;
  aml_checklist: string | null;
  commentary: string | null;
  last_modified_by: number | null;
  created_at: string;
  updated_at: string;
}

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  remittance: "Перевод",
  notional: "Тело платежа",
  fees: "Комиссии",
  test: "Тестовый перевод",
  return: "Возврат",
};

PAYMENT_TYPE_LABEL.body_plus_fee = "Тело + комиссия";

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  review: "На проверке",
  sent: "Отправлен",
  aml_package_required: "AML-пакет",
  received: "Получен",
  returned: "Возвращён",
  cancelled: "Отменён",
};

const PAYMENT_STATUS_CLS: Record<string, string> = {
  review: "text-tg-hint",
  sent: "text-tg-text",
  aml_package_required: "text-tg-hint",
  received: "text-tg-text",
  returned: "text-tg-hint",
  cancelled: "text-tg-hint",
};

const ALL_STATUSES = ["review", "sent", "aml_package_required", "received", "returned", "cancelled"] as const;

const KNOWN_PAYMENT_TYPES = ["remittance", "notional", "fees", "body_plus_fee", "test", "return"] as const;

const calmPrimaryButton =
  "rounded-lg bg-tg-secondaryBg text-tg-text border border-tg-secondaryBg text-sm font-medium disabled:opacity-50";
const calmCompactButton =
  "text-[11px] px-2 py-0.5 rounded bg-tg-secondaryBg text-tg-text border border-tg-secondaryBg disabled:opacity-50";

function PaymentEditModal({ payment, deal, onClose, onSaved, onDeleted, canDelete }: {
  payment: DealPayment;
  deal: DealDetail;
  onClose: () => void;
  onSaved: (updated: DealPayment) => void;
  onDeleted: (id: number) => void;
  canDelete: boolean;
}) {
  const [paymentType, setPaymentType] = useState(payment.payment_type);
  const [customType, setCustomType] = useState(
    KNOWN_PAYMENT_TYPES.includes(payment.payment_type as typeof KNOWN_PAYMENT_TYPES[number]) ? "" : payment.payment_type
  );
  const initialActual = paymentActualAmount(payment, deal);
  const [direction, setDirection] = useState<"receivable" | "payable">(
    payment.direction ?? (isReceivable(payment) ? "receivable" : "payable"),
  );
  const [amountInput, setAmountInput] = useState(
    fmtMoneyInput(payment.amount ?? initialActual.amount ?? ""),
  );
  const [amountCurrency, setAmountCurrency] = useState(
    payment.amount_currency ?? initialActual.currency ?? "",
  );
  const [newStatus, setNewStatus] = useState(payment.status);
  const [fromCpId, setFromCpId] = useState<string>(payment.from_counterparty_id?.toString() ?? "");
  const [fromBankName, setFromBankName] = useState(payment.from_bank_name ?? "");
  const [fromAccountNo, setFromAccountNo] = useState(payment.from_account_no ?? "");
  const [fromSwift, setFromSwift] = useState(payment.from_swift ?? "");
  const [fromBic, setFromBic] = useState(payment.from_bic ?? "");
  const [toCpId, setToCpId] = useState<string>(payment.to_counterparty_id?.toString() ?? "");
  const [toBankName, setToBankName] = useState(payment.to_bank_name ?? "");
  const [toAccountNo, setToAccountNo] = useState(payment.to_account_no ?? "");
  const [toSwift, setToSwift] = useState(payment.to_swift ?? "");
  const [toBic, setToBic] = useState(payment.to_bic ?? "");
  const [commentary, setCommentary] = useState(payment.commentary ?? "");
  const [aml, setAml] = useState<AMLItem[]>(() =>
    payment.aml_checklist ? JSON.parse(payment.aml_checklist) : []
  );
  const [dicts, setDicts] = useState<Dictionaries | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<Dictionaries>("/api/deals/_meta/dictionaries").then(setDicts).catch(() => {});
  }, []);

  // Flat list of all bank names known in the system for datalist suggestions.
  const allBankNames = dicts
    ? Object.values(dicts.banks_by_company).flat().map(b => b.name)
    : [];
  const selectedPaymentType = paymentType === "__custom__" ? customType.trim() : paymentType;
  const previewAmountText = parseMoneyInput(amountInput);
  const previewAmount = previewAmountText === "" || Number.isNaN(Number(previewAmountText))
    ? null
    : Number(previewAmountText);
  const actual = paymentActualAmount(
    {
      ...payment,
      payment_type: selectedPaymentType || payment.payment_type,
      direction,
      amount: previewAmount,
      amount_currency: amountCurrency.trim() || payment.amount_currency,
    },
    deal,
  );

  const handleDelete = async () => {
    if (!confirm("Удалить это поручение? Действие необратимо.")) return;
    setDeleting(true);
    try {
      await api.del(`/api/payment-legs/${payment.id}`);
      onDeleted(payment.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setDeleting(false);
    }
  };

  const toggleAml = (idx: number) => setAml((prev) => prev.map((item, i) => i === idx ? { ...item, included: !item.included } : item));
  const updateAmlNote = (idx: number, val: string) => setAml((prev) => prev.map((item, i) => i === idx ? { ...item, name: val } : item));

  const handleSave = async () => {
    const preParsedAmount = parseMoneyInput(amountInput);
    const preAmountValue = preParsedAmount === "" ? null : Number(preParsedAmount);
    if (preParsedAmount !== "" && Number.isNaN(preAmountValue)) {
      setErr("Укажите корректную сумму платежа");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const resolvedType = paymentType === "__custom__" ? customType.trim() : paymentType;
      if (!resolvedType) { setErr("Укажите тип платежа"); setBusy(false); return; }
      const parsedAmount = parseMoneyInput(amountInput);
      const amountValue = parsedAmount === "" ? null : Number(parsedAmount);
      if (parsedAmount !== "" && Number.isNaN(amountValue)) {
        setErr("Укажите корректную сумму платежа");
        setBusy(false);
        return;
      }
      await api.put(`/api/payment-legs/${payment.id}/details`, {
        payment_type: resolvedType,
        direction,
        amount: amountValue,
        amount_currency: amountCurrency.trim().toUpperCase() || actual.currency,
        from_counterparty_id: fromCpId ? parseInt(fromCpId, 10) : null,
        from_bank: { bank_name: fromBankName || null, account_no: fromAccountNo || null, swift: fromSwift || null, bic: fromBic || null },
        to_counterparty_id: toCpId ? parseInt(toCpId, 10) : null,
        to_bank: { bank_name: toBankName || null, account_no: toAccountNo || null, swift: toSwift || null, bic: toBic || null },
        aml_checklist: aml,
        commentary: commentary || null,
      });
      if (newStatus !== payment.status) {
        await api.put(`/api/payment-legs/${payment.id}/status`, { status: newStatus });
      }
      const updated = await api.get<DealPayment>(`/api/payment-legs/${payment.id}`);
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-3">
      <div className="bg-tg-sectionBg w-full max-w-lg rounded-xl p-4 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">
            {PAYMENT_TYPE_LABEL[payment.payment_type] ?? payment.payment_type}
          </h2>
          <button onClick={onClose} className="text-tg-hint text-sm">✕</button>
        </div>
        <div>
          <div className="text-xs text-tg-hint mb-1">Статус</div>
          <select value={newStatus} onChange={(e) => setNewStatus(e.target.value as DealPayment["status"])}
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm">
            {ALL_STATUSES.map((s) => <option key={s} value={s}>{PAYMENT_STATUS_LABEL[s]}</option>)}
          </select>
        </div>
        <div>
          <div className="text-xs text-tg-hint mb-1">Тип платежа</div>
          <select
            value={KNOWN_PAYMENT_TYPES.includes(paymentType as typeof KNOWN_PAYMENT_TYPES[number]) ? paymentType : "__custom__"}
            onChange={(e) => { setPaymentType(e.target.value); if (e.target.value !== "__custom__") setCustomType(""); }}
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          >
            {KNOWN_PAYMENT_TYPES.map((t) => <option key={t} value={t}>{PAYMENT_TYPE_LABEL[t] ?? t}</option>)}
            <option value="__custom__">✏️ Произвольный тип…</option>
          </select>
          {(paymentType === "__custom__" || !KNOWN_PAYMENT_TYPES.includes(paymentType as typeof KNOWN_PAYMENT_TYPES[number])) && (
            <input
              value={customType}
              onChange={(e) => setCustomType(e.target.value)}
              placeholder="Введите тип платежа"
              className="w-full mt-2 px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          )}
        </div>
        <div>
          <div className="text-xs text-tg-hint mb-1">Сумма</div>
          <div className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text">
            <div className="text-base font-semibold tabular-nums">
              {actual.amount !== null
                ? `${fmtMoney(actual.amount)} ${actual.currency}`
                : "—"}
            </div>
            <div className="text-[11px] text-tg-hint mt-0.5">{actual.label}</div>
          </div>
        </div>
        <div>
          <div className="text-xs text-tg-hint mb-1">Сторона платежа</div>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as "receivable" | "payable")}
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          >
            <option value="receivable">Получение</option>
            <option value="payable">Оплата</option>
          </select>
        </div>
        <div>
          <div className="text-xs text-tg-hint mb-1">Сумма платежа</div>
          <div className="grid grid-cols-[1fr_96px] gap-2">
            <input
              value={amountInput}
              onChange={(e) => setAmountInput(fmtMoneyInput(e.target.value))}
              inputMode="decimal"
              placeholder="0"
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm tabular-nums"
            />
            <input
              value={amountCurrency}
              onChange={(e) => setAmountCurrency(e.target.value.toUpperCase())}
              placeholder={actual.currency}
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm uppercase"
            />
          </div>
        </div>
        {/* Datalist for bank name suggestions */}
        <datalist id="bank-names-list">
          {allBankNames.map((n, i) => <option key={i} value={n} />)}
        </datalist>
        <div>
          <div className="text-xs font-semibold text-tg-hint mb-2 uppercase tracking-wide">Плательщик</div>
          <div className="space-y-2">
            <select
              value={fromCpId}
              onChange={(e) => setFromCpId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            >
              <option value="">— Контрагент не выбран —</option>
              {(dicts?.counterparties ?? []).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <input value={fromBankName} onChange={(e) => setFromBankName(e.target.value)}
              placeholder="Банк плательщика" list="bank-names-list"
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm" />
            <input value={fromAccountNo} onChange={(e) => setFromAccountNo(e.target.value)} placeholder="Номер счёта"
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input value={fromSwift} onChange={(e) => setFromSwift(e.target.value)} placeholder="SWIFT"
                className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm" />
              <input value={fromBic} onChange={(e) => setFromBic(e.target.value)} placeholder="BIC"
                className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm" />
            </div>
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold text-tg-hint mb-2 uppercase tracking-wide">Получатель</div>
          <div className="space-y-2">
            <select
              value={toCpId}
              onChange={(e) => setToCpId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            >
              <option value="">— Контрагент не выбран —</option>
              {(dicts?.counterparties ?? []).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <input value={toBankName} onChange={(e) => setToBankName(e.target.value)}
              placeholder="Банк получателя" list="bank-names-list"
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm" />
            <input value={toAccountNo} onChange={(e) => setToAccountNo(e.target.value)} placeholder="Номер счёта"
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input value={toSwift} onChange={(e) => setToSwift(e.target.value)} placeholder="SWIFT"
                className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm" />
              <input value={toBic} onChange={(e) => setToBic(e.target.value)} placeholder="BIC"
                className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm" />
            </div>
          </div>
        </div>
        {aml.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-tg-hint mb-2 uppercase tracking-wide">AML-пакет</div>
            <div className="space-y-2">
              {aml.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <button onClick={() => toggleAml(i)}
                    className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 text-xs ${item.included ? "bg-green-500 border-green-500 text-white" : "border-slate-400"}`}>
                    {item.included && "✓"}
                  </button>
                  {item.optional
                    ? <input value={item.name} onChange={(e) => updateAmlNote(i, e.target.value)} placeholder="Заметка оператора"
                        className="flex-1 px-2 py-1 rounded bg-tg-secondaryBg text-tg-text text-sm" />
                    : <span className="text-sm">{item.name}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <div className="text-xs text-tg-hint mb-1">Комментарий</div>
          <textarea value={commentary} onChange={(e) => setCommentary(e.target.value)} rows={2}
            placeholder="Произвольный комментарий"
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm" />
        </div>
        <div>
          <div className="text-xs text-tg-hint mb-1">Сторона платежа</div>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as "receivable" | "payable")}
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          >
            <option value="receivable">Получение</option>
            <option value="payable">Оплата</option>
          </select>
        </div>
        <div>
          <div className="text-xs text-tg-hint mb-1">Сумма платежа</div>
          <div className="grid grid-cols-[1fr_96px] gap-2">
            <input
              value={amountInput}
              onChange={(e) => setAmountInput(fmtMoneyInput(e.target.value))}
              inputMode="decimal"
              placeholder="0"
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm tabular-nums"
            />
            <input
              value={amountCurrency}
              onChange={(e) => setAmountCurrency(e.target.value.toUpperCase())}
              placeholder={deal.currency || deal.base_currency}
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm uppercase"
            />
          </div>
        </div>
        {err && <div className="text-xs text-red-500">{err}</div>}
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg bg-tg-secondaryBg text-sm">Отмена</button>
          <button disabled={busy} onClick={handleSave}
            className={`flex-1 py-2 ${calmPrimaryButton}`}>
            {busy ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
        {canDelete && (
          <button
            disabled={deleting}
            onClick={handleDelete}
            className="w-full py-2 rounded-lg bg-red-50 text-red-700 border border-red-100 text-sm font-medium disabled:opacity-50 mt-1"
          >
            {deleting ? "Удаление…" : "🗑 Удалить поручение"}
          </button>
        )}
      </div>
    </div>
  );
}

function AddPaymentModal({ dealId, deal, onClose, onCreated }: {
  dealId: number;
  deal: DealDetail;
  onClose: () => void;
  onCreated: (p: DealPayment) => void;
}) {
  const [paymentType, setPaymentType] = useState("remittance");
  const [customType, setCustomType] = useState("");
  const [direction, setDirection] = useState<"receivable" | "payable">("payable");
  const [amountInput, setAmountInput] = useState("");
  const [amountCurrency, setAmountCurrency] = useState(deal.currency || deal.base_currency);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const parsedAmount = parseMoneyInput(amountInput);
  const amountValue = parsedAmount === "" || Number.isNaN(Number(parsedAmount))
    ? null
    : Number(parsedAmount);

  const handleCreate = async () => {
    const resolvedType = paymentType === "__custom__" ? customType.trim() : paymentType;
    if (!resolvedType) { setErr("Укажите тип платежа"); return; }
    setBusy(true);
    setErr(null);
    try {
      const created = await api.post<DealPayment>(`/api/payment-legs/deals/${dealId}`, {
        payment_type: resolvedType,
        direction,
        amount: amountValue,
        amount_currency: amountCurrency.trim().toUpperCase() || deal.currency || deal.base_currency,
      });
      onCreated(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-3">
      <div className="bg-tg-sectionBg w-full max-w-md rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">➕ Новый платёж</h2>
          <button onClick={onClose} className="text-tg-hint text-sm">✕</button>
        </div>
        <div>
          <div className="text-xs text-tg-hint mb-1">Тип платежа</div>
          <select
            value={paymentType}
            onChange={(e) => { setPaymentType(e.target.value); if (e.target.value !== "__custom__") setCustomType(""); }}
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          >
            {KNOWN_PAYMENT_TYPES.map((t) => (
              <option key={t} value={t}>{PAYMENT_TYPE_LABEL[t] ?? t}</option>
            ))}
            <option value="__custom__">✏️ Произвольный тип…</option>
          </select>
          {paymentType === "__custom__" && (
            <input
              value={customType}
              onChange={(e) => setCustomType(e.target.value)}
              placeholder="Введите тип платежа"
              className="w-full mt-2 px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          )}
        </div>
        {err && <div className="text-xs text-red-500">{err}</div>}
        <div>
          <div className="text-xs text-tg-hint mb-1">Сторона платежа</div>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as "receivable" | "payable")}
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          >
            <option value="receivable">Получение</option>
            <option value="payable">Оплата</option>
          </select>
        </div>
        <div>
          <div className="text-xs text-tg-hint mb-1">Сумма платежа</div>
          <div className="grid grid-cols-[1fr_96px] gap-2">
            <input
              value={amountInput}
              onChange={(e) => setAmountInput(fmtMoneyInput(e.target.value))}
              inputMode="decimal"
              placeholder="0"
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm tabular-nums"
            />
            <input
              value={amountCurrency}
              onChange={(e) => setAmountCurrency(e.target.value.toUpperCase())}
              placeholder={deal.currency || deal.base_currency}
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm uppercase"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg bg-tg-secondaryBg text-sm">Отмена</button>
          <button
            disabled={busy}
            onClick={handleCreate}
            className={`flex-1 py-2 ${calmPrimaryButton}`}
          >
            {busy ? "Создание…" : "Создать"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface NamedItem { id: number; name: string }
interface Dictionaries {
  companies: NamedItem[];
  banks_by_company: Record<string, NamedItem[]>;
  counterparties: NamedItem[];
  operators: NamedItem[];
  subcontractors: NamedItem[];
  subcontractor_banks_by_sub: Record<string, NamedItem[]>;
}

interface InflowRow {
  id: number;
  amount: number;
  received_at: string | null;
  note: string | null;
  recorded_by: number | null;
}

interface TimelineRow {
  key: string;
  label: string;
  at: string | null;
  done: boolean;
  kind: "info" | "good" | "bad";
  reason?: string | null;
}

interface DealDetail {
  id: number;
  instruction_no: number | null;
  deal_type: string | null;
  deal_format: string | null;
  status: string | null;
  amount: number | null;
  currency: string;
  base_currency: string;
  rate: number | null;
  fee_percent: number | null;
  fixed_commission_amount: number | null;
  fixed_commission_currency: string | null;
  fixed_commission_rate: number | null;
  deal_date: string | null;
  fixed_at: string | null;
  instruction_received_at: string | null;
  payment_confirmed_at: string | null;
  client_receipt_confirmed_at: string | null;
  closed_at: string | null;
  pending_approval: boolean;
  cancellation_reason: string | null;
  cancelled_at_status: string | null;
  cancelled_by_telegram_id: number | null;
  operator_id: number | null;
  operator_name: string | null;
  company_id: number | null;
  company: string | null;
  bank_id: number | null;
  bank: string | null;
  counterparty_id: number | null;
  counterparty: string | null;
  subcontractor_id: number | null;
  subcontractor_name: string | null;
  subcontractor_bank_id: number | null;
  subcontractor_bank: string | null;
  incoming_rub_bank_id: number | null;
  incoming_rub_bank: string | null;
  deadline: string | null;
  expected_rub_date: string | null;
  planned_fix_date: string | null;
  created_at: string | null;
  notes: string | null;
  computed: {
    main_rate: number | null;
    body_rub: number | null;
    fee_rub: number | null;
    total_rub: number | null;
  };
  inflows: InflowRow[];
  timeline: TimelineRow[];
  legs: PaymentLeg[];
  is_trashed?: boolean;
  deleted_at?: string | null;
  deletion_reason?: string | null;
  actions: {
    can_edit: boolean;
    can_cancel: boolean;
    can_delete: boolean;
    can_restore?: boolean;
    can_purge?: boolean;
    can_confirm_client: boolean;
  };
}

export interface PaymentLeg {
  direction: "incoming" | "outgoing" | "exchange";
  leg_kind: string;
  label: string;
  sequence: number;
  amount: number;
  currency: string;
  payer_name?: string | null;
  payer_bank_name?: string | null;
  payee_name?: string | null;
  payee_bank_name?: string | null;
  planned_date: string | null;
  executed_date: string | null;
  status: "planned" | "partial" | "confirmed";
  settled_amount: number;
  balance_role: "debit" | "credit" | "transit";
  balance_remaining: number;
  note?: string | null;
  fee_percent?: number | null;
  lot_id?: number | null;
  rate_to_rub?: number | null;
}

interface Props {
  dealId: number;
  me: MeResponse;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}

/** Classify a payment as receivable (client → us) or payable (us → beneficiary). */
function isReceivable(p: DealPayment): boolean {
  if (p.direction) return p.direction === "receivable";
  return p.payment_type === "return"
    || p.payment_type === "notional"
    || p.payment_type === "fees";
}

const INTERNAL_ACCOUNT = "INTERNAL_ACCOUNT";

function paymentActualAmount(p: DealPayment, deal: DealDetail): {
  label: string;
  amount: number | null;
  currency: string;
} {
  const fallbackCurrency = p.amount_currency || deal.base_currency || deal.currency;
  if (p.amount !== null && p.amount !== undefined) {
    return {
      label: "Сумма платежа",
      amount: p.amount,
      currency: fallbackCurrency,
    };
  }
  if (deal.deal_type === "export" && p.payment_type === "notional") {
    return {
      label: "Тело платежа",
      amount: deal.computed.body_rub,
      currency: deal.base_currency,
    };
  }
  if (deal.deal_type === "export" && p.payment_type === "remittance") {
    return {
      label: "Перевод",
      amount: deal.amount,
      currency: deal.currency,
    };
  }
  if (deal.deal_type === "export" && p.payment_type === "remittance") {
    return {
      label: "Рублёвый платёж",
      amount: deal.amount,
      currency: deal.currency,
    };
  }
  if (deal.deal_type === "export" && p.payment_type === "body_plus_fee") {
    return {
      label: "Тело + комиссия",
      amount: deal.computed.total_rub,
      currency: deal.base_currency,
    };
  }
  if (p.payment_type === "notional") {
    return {
      label: "Тело платежа",
      amount: deal.computed.body_rub,
      currency: deal.base_currency,
    };
  }
  if (p.payment_type === "fees") {
    const fixedCcy = deal.fixed_commission_currency || deal.base_currency;
    const fixed = deal.fixed_commission_amount;
    if (
      deal.computed.fee_rub !== null &&
      fixed !== null &&
      fixedCcy === deal.base_currency
    ) {
      return {
        label: "Комиссия",
        amount: deal.computed.fee_rub + fixed,
        currency: deal.base_currency,
      };
    }
    if (deal.computed.fee_rub !== null) {
      return {
        label: "Комиссия",
        amount: deal.computed.fee_rub,
        currency: deal.base_currency,
      };
    }
    return {
      label: "Комиссия",
      amount: fixed,
      currency: fixedCcy,
    };
  }
  if (p.payment_type === "return") {
    return {
      label: "Возврат",
      amount: deal.computed.total_rub,
      currency: deal.base_currency,
    };
  }
  return {
    label: "Перевод",
    amount: p.payment_type === "body_plus_fee" ? deal.computed.total_rub : deal.amount,
    currency: p.payment_type === "body_plus_fee" ? deal.base_currency : deal.currency,
  };
}

/** Одна строка реквизитов — имя + счёт в одну строку или INTERNAL_ACCOUNT. */
function PartyRow({ name, account, highlight }: { name: string; account?: string | null; highlight?: boolean }) {
  const isInternal = name === INTERNAL_ACCOUNT;
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      {isInternal ? (
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-tg-secondaryBg text-tg-text"
        >
          ⬢ {name}
        </span>
      ) : (
        <>
          <span className={`text-[11px] truncate ${highlight ? "font-semibold" : ""}`}>{name}</span>
          {account && <span className="text-[10px] text-tg-hint truncate">{account}</span>}
        </>
      )}
    </div>
  );
}

/** Ячейка платёжного поручения в T-счёте.
 *  Слева (receivable): плательщик → INTERNAL_ACCOUNT
 *  Справа (payable):    INTERNAL_ACCOUNT → бенефициар */
function PaymentCell({ payment: p, side, onEdit, deal }: {
  payment: DealPayment;
  side: "left" | "right";
  onEdit: (p: DealPayment) => void;
  deal: DealDetail;
}) {
  const aml: AMLItem[] = p.aml_checklist ? JSON.parse(p.aml_checklist) : [];
  const amlDone = aml.filter(i => i.included).length;
  const amlTotal = aml.filter(i => !i.optional).length;
  const actual = paymentActualAmount(p, deal);

  // Для receivable: from=плательщик, to=INTERNAL_ACCOUNT
  // Для payable:    from=INTERNAL_ACCOUNT, to=бенефициар
  const fromName = side === "left" ? (p.from_bank_name || "Контрагент") : INTERNAL_ACCOUNT;
  const fromAcct = side === "left" ? p.from_account_no : null;
  const toName   = side === "left" ? INTERNAL_ACCOUNT : (p.to_bank_name || "Бенефициар");
  const toAcct   = side === "left" ? null : p.to_account_no;

  return (
    <div className="bg-tg-bg rounded-lg p-2 space-y-1.5">
      {/* Тип */}
      <div className="flex items-center justify-between gap-1">
        <span className="text-[11px] font-semibold text-tg-text">
          {PAYMENT_TYPE_LABEL[p.payment_type] ?? p.payment_type}
        </span>
      </div>

      <div className="flex items-baseline justify-between gap-2 rounded bg-tg-secondaryBg px-2 py-1">
        <span className="text-[10px] text-tg-hint">Сумма</span>
        <div className="text-right min-w-0">
          <div className="text-[12px] font-bold tabular-nums text-tg-text">
            {actual.amount !== null
              ? `${fmtMoney(actual.amount)} ${actual.currency}`
              : "—"}
          </div>
          <div className="text-[9px] text-tg-hint">{actual.label}</div>
        </div>
      </div>

      {/* Поток: от → к */}
      <div className="flex items-center gap-1 text-[10px]">
        <PartyRow name={fromName} account={fromAcct} highlight={side === "right"} />
        <span className="text-tg-hint shrink-0">→</span>
        <PartyRow name={toName} account={toAcct} highlight={side === "left"} />
      </div>

      {/* Статус + AML + редактирование */}
      <div className="flex items-center gap-1 flex-wrap">
        <span
          className={`text-[9px] font-medium whitespace-nowrap ${PAYMENT_STATUS_CLS[p.status] ?? "text-tg-hint"}`}
        >
          {PAYMENT_STATUS_LABEL[p.status] ?? p.status}
        </span>
        {amlTotal > 0 && (
          <span className="text-[9px] font-medium text-tg-hint">
            AML {amlDone}/{amlTotal}
          </span>
        )}
        <button
          onClick={() => onEdit(p)}
          className="ml-auto text-[9px] font-medium underline hover:no-underline text-tg-hint"
        >
          ✏️ Изм.
        </button>
      </div>
    </div>
  );
}

/** T-счёт («самолётик») для платёжных поручений.
 *  Слева (дебиторка): плательщик → INTERNAL_ACCOUNT
 *  Справа (кредиторка): INTERNAL_ACCOUNT → бенефициар */
function PaymentLegsTAccount({ payments, onEdit, deal }: {
  payments: DealPayment[];
  onEdit: (p: DealPayment) => void;
  deal: DealDetail;
}) {
  const receivable = payments.filter(isReceivable);
  const payable = payments.filter(p => !isReceivable(p));

  return (
    <div className="rounded-lg overflow-hidden border border-tg-secondaryBg">
      {/* Шапка */}
      <div className="grid grid-cols-2 text-center text-[11px] font-semibold border-b border-tg-secondaryBg">
        <div
          className="py-1.5 border-r border-tg-secondaryBg bg-tg-bg text-tg-text"
        >
          Дебиторка
        </div>
        <div
          className="py-1.5 bg-tg-bg text-tg-text"
        >
          Кредиторка
        </div>
      </div>

      {/* Тело T-счёта */}
      {payments.length === 0 ? (
        <div className="text-[11px] text-tg-hint text-center py-5">
          Нет поручений. Нажмите «⚡ Авто» для создания или «＋ Добавить» вручную.
        </div>
      ) : (
        <div className="grid grid-cols-2 relative">
          <div className="absolute inset-y-0 left-1/2 w-px bg-tg-secondaryBg" />
          <div className="p-1.5 space-y-1.5 border-r border-tg-secondaryBg">
            {receivable.length === 0 ? (
              <div className="text-[11px] text-tg-hint text-center py-3">—</div>
            ) : (
              receivable.map(p => <PaymentCell key={p.id} payment={p} side="left" onEdit={onEdit} deal={deal} />)
            )}
          </div>
          <div className="p-1.5 space-y-1.5">
            {payable.length === 0 ? (
              <div className="text-[11px] text-tg-hint text-center py-3">—</div>
            ) : (
              payable.map(p => <PaymentCell key={p.id} payment={p} side="right" onEdit={onEdit} deal={deal} />)
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label, value, hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-tg-secondaryBg last:border-b-0">
      <div className="text-xs text-tg-hint pt-0.5">{label}</div>
      <div className="text-sm text-right min-w-0 break-words">
        <div>{value ?? "—"}</div>
        {hint && <div className="text-[10px] text-tg-hint mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  // YYYY-MM-DD → DD.MM.YYYY; ISO datetime → DD.MM.YYYY HH:MM
  if (iso.length === 10) return iso.split("-").reverse().join(".");
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function DealCard({
  dealId, me, onClose, onChanged, onDeleted,
}: Props) {
  const [d, setD] = useState<DealDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [payments, setPayments] = useState<DealPayment[]>([]);
  const [editingPayment, setEditingPayment] = useState<DealPayment | null>(null);
  const [creatingPayments, setCreatingPayments] = useState(false);
  const [addingPayment, setAddingPayment] = useState(false);

  useBackButton(onClose, true);

  const reload = () => {
    setLoading(true);
    setError(null);
    api
      .get<DealDetail>(`/api/deals/${dealId}`)
      .then(setD)
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [dealId]);

  // Load payment legs for this deal
  useEffect(() => {
    api.get<DealPayment[]>(`/api/payment-legs/deals/${dealId}`)
      .then(setPayments)
      .catch(() => setPayments([]));
  }, [dealId]);

  if (loading) {
    return (
      <div className="text-tg-hint p-4 animate-pulse">Загрузка…</div>
    );
  }
  if (error || !d) {
    return (
      <div className="space-y-3">
        <button onClick={onClose} className="text-sm text-tg-link">← Назад</button>
        <ErrorBox text={error || "Не удалось загрузить заявку."} />
      </div>
    );
  }

  const ro = !d.actions.can_edit; // read-only
  const dealCode = `D-${String(d.id).padStart(6, "0")}`;

  const confirmClient = async () => {
    setBusy("confirm");
    setMsg(null);
    try {
      await api.post(`/api/deals/${d.id}/confirm_client_receipt`);
      onChanged();
      reload();
      setMsg("✅ Отмечено: клиент подтвердил получение.");
    } catch (e) {
      setMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    if (!confirm(
      `Полностью удалить ${dealCode} из системы?\n\n` +
      "Удалятся: сама заявка, привязанные RubInflows, FxConsumptions. " +
      "Это необратимо. Действие записывается в audit-log.",
    )) return;
    setBusy("delete");
    setMsg(null);
    try {
      const res = await api.del<{ deal: number; inflows: number; consumptions: number }>(
        `/api/deals/${d.id}`,
      );
      onDeleted();
      // Покажем сводку через alert и закроем — onDeleted уже вызвал onClose.
      alert(
        `Удалено: заявка ${res.deal}, поступления ${res.inflows}, ` +
        `списания FX ${res.consumptions}.`,
      );
    } catch (e) {
      setMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
      setBusy(null);
    }
  };

  const submitCancel = async () => {
    if (!cancelReason.trim()) {
      setMsg("Укажите причину отмены.");
      return;
    }
    setBusy("cancel");
    setMsg(null);
    try {
      await api.post(`/api/deals/${d.id}/cancel`, { reason: cancelReason.trim() });
      setCancelOpen(false);
      setCancelReason("");
      onChanged();
      reload();
      setMsg("✅ Заявка отменена.");
    } catch (e) {
      setMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3 pb-12">
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="text-sm text-tg-link">← К реестру</button>
        <div className="text-xs text-tg-hint">{dealCode}</div>
      </div>

      <div>
        <h1 className="text-xl font-semibold">
          {d.deal_type === "import" ? "📥" : d.deal_type === "export" ? "📤" : "📄"}{" "}
          Заявка {dealCode}
          {d.instruction_no && <span className="ml-2 text-tg-hint text-sm">№ {d.instruction_no}</span>}
        </h1>
        <div className="text-xs text-tg-hint mt-0.5">
          Тип: {d.deal_type || "—"} · Формат: {d.deal_format || "—"} ·
          Статус: <strong>{d.status || "—"}</strong>
        </div>
        {ro && me.permissions.key_analytic_only && (
          <div className="mt-2 inline-block px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 text-[11px] font-medium">
            🔒 Обезличенный режим — имена скрыты кодами
          </div>
        )}
      </div>

      {msg && (
        <div className="text-xs text-tg-hint bg-tg-sectionBg rounded-lg px-3 py-2">
          {msg}
        </div>
      )}

      {/* 1. Детали поручения */}
      <section className="bg-tg-sectionBg rounded-xl p-3">
        <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-1">
          Детали поручения
        </div>
        <Field label="№ заявки" value={dealCode} />
        <Field label="№ поручения" value={d.instruction_no ?? "—"} />
        <Field label="Оператор" value={d.operator_name || "—"} />
        <Field label="Дата заявки" value={fmtDate(d.deal_date)} />
        <Field label="Дата валютирования" value={fmtDate(d.expected_rub_date)} />
        <Field label="Дедлайн" value={fmtDate(d.deadline)} />
        <Field label="Тип операции" value={d.deal_type || "—"} />
        <Field label="Формат расчёта" value={d.deal_format || "—"} />
        <Field label="Валюта перевода" value={d.currency} />
        <Field label="Базовая валюта" value={d.base_currency} />
        <Field
          label="Сумма перевода"
          value={d.amount !== null ? `${fmtMoney(d.amount)} ${d.currency}` : "—"}
        />
        <Field
          label="Согласованный курс"
          value={d.rate !== null ? d.rate.toLocaleString("ru-RU") : "—"}
        />
        <Field
          label="Вознаграждение, %"
          value={d.fee_percent !== null ? `${d.fee_percent}%` : "—"}
        />
        <Field
          label="Фикс. комиссия"
          value={
            d.fixed_commission_amount !== null
              ? `${fmtMoney(d.fixed_commission_amount)} ${d.fixed_commission_currency || ""}`
              : "—"
          }
        />
        <Field label="Контрагент" value={d.counterparty || "—"} />
        <Field label="Компания-плательщик" value={d.company || "—"} />
        <Field label="Банк компании" value={d.bank || "—"} />
        {d.subcontractor_name && (
          <Field label="Субподрядчик" value={d.subcontractor_name} />
        )}
        {d.subcontractor_bank && (
          <Field label="Банк субподрядчика" value={d.subcontractor_bank} />
        )}
        {d.incoming_rub_bank && (
          <Field label="Банк-получатель RUB" value={d.incoming_rub_bank} />
        )}
        <Field label="Комментарий" value={d.notes || "—"} />
      </section>

      {/* 2. Расчёт (read-only) */}
      <section className="bg-tg-sectionBg rounded-xl p-3">
        <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-1">
          Расчёт
        </div>
        <Field
          label="Курс (основной)"
          value={
            d.computed.main_rate !== null
              ? d.computed.main_rate.toLocaleString("ru-RU")
              : "—"
          }
        />
        <Field
          label="Тело, ₽"
          value={d.computed.body_rub !== null ? fmtMoney(d.computed.body_rub) : "—"}
        />
        <Field
          label="Вознаграждение, ₽"
          value={d.computed.fee_rub !== null ? fmtMoney(d.computed.fee_rub) : "—"}
        />
        <Field
          label="Итого ожидание, ₽"
          value={
            d.computed.total_rub !== null ? fmtMoney(d.computed.total_rub) : "—"
          }
        />
      </section>

      {/* 3. Хронология */}
      <section className="bg-tg-sectionBg rounded-xl p-3">
        <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-2">
          Хронология
        </div>
        <ol className="space-y-2">
          {d.timeline.map((t) => (
            <li key={t.key} className="flex items-start gap-2">
              <div
                className={
                  "mt-0.5 w-3 h-3 rounded-full shrink-0 " +
                  (t.done
                    ? t.kind === "bad"
                      ? "bg-red-500"
                      : t.kind === "good"
                      ? "bg-green-500"
                      : "bg-blue-500"
                    : "bg-slate-300 dark:bg-slate-600")
                }
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm">
                  {t.label}
                  {!t.done && (
                    <span className="ml-1 text-tg-hint text-xs">(ожидается)</span>
                  )}
                </div>
                {t.at && (
                  <div className="text-[11px] text-tg-hint">{fmtDate(t.at)}</div>
                )}
                {t.reason && (
                  <div className="text-[11px] text-red-600 mt-0.5">
                    причина: {t.reason}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* 3b. Платёжные ноги — T-счёт + управляемые поручения. */}
      <section className="bg-tg-sectionBg rounded-xl p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader">
            Платёжные ноги
          </div>
          <div className="flex gap-1.5">
            {payments.length === 0 && (
              <button
                disabled={creatingPayments}
                onClick={async () => {
                  setCreatingPayments(true);
                  try {
                    const created = await api.post<DealPayment[]>(`/api/payment-legs/deals/${dealId}/create`);
                    setPayments(created);
                  } catch { /* ignore */ } finally { setCreatingPayments(false); }
                }}
                className={calmCompactButton}
              >
                {creatingPayments ? "…" : "⚡ Авто"}
              </button>
            )}
            <button
              onClick={() => setAddingPayment(true)}
              className={calmCompactButton}
            >
              ＋ Добавить
            </button>
          </div>
        </div>

        <PaymentLegsTAccount
          payments={payments}
          onEdit={(p) => setEditingPayment(p)}
          deal={d}
        />
      </section>

      {/* 4. Поступления */}
      {d.inflows.length > 0 && (
        <section className="bg-tg-sectionBg rounded-xl p-3">
          <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-2">
            Поступления ({d.inflows.length})
          </div>
          <div className="space-y-1">
            {d.inflows.map((r) => (
              <div key={r.id} className="flex justify-between items-baseline text-sm">
                <div>{fmtDate(r.received_at)}</div>
                <div className="font-semibold">{fmtMoney(r.amount)} ₽</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 4b. Корзина: метаданные удаления + кнопки восстановления. */}
      {d.is_trashed && (
        <section className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-3 space-y-1">
          <div className="text-sm font-medium text-red-800 dark:text-red-200">
            🗑 В корзине
          </div>
          <div className="text-xs text-red-700 dark:text-red-300">
            Удалена: {fmtDate(d.deleted_at || null)}
          </div>
          {d.deletion_reason && (
            <div className="text-xs text-red-700 dark:text-red-300">
              Причина: {d.deletion_reason}
            </div>
          )}
        </section>
      )}

      {/* 5. Действия */}
      <section className="space-y-2">
        {d.actions.can_restore && (
          <button
            disabled={busy === "restore"}
            onClick={async () => {
              setBusy("restore");
              try {
                await api.post(`/api/deals/${d.id}/restore`);
                onChanged(); reload();
                setMsg("✅ Заявка восстановлена из корзины.");
              } catch (e) {
                setMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
              } finally { setBusy(null); }
            }}
            className={`w-full py-2.5 ${calmPrimaryButton}`}
          >
            ♻️ Восстановить из корзины
          </button>
        )}
        {d.actions.can_purge && (
          <button
            disabled={busy === "purge"}
            onClick={async () => {
              if (!confirm(
                `Окончательно удалить ${dealCode}?\n\n` +
                "После этой операции восстановить нельзя. " +
                "Зависимые RubInflows и FxConsumption будут удалены.",
              )) return;
              setBusy("purge");
              try {
                await api.del<{ purged: boolean }>(
                  `/api/deals/${d.id}/purge`,
                );
                onDeleted();
              } catch (e) {
                setMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
                setBusy(null);
              }
            }}
            className="w-full py-2.5 rounded-lg bg-red-50 text-red-700 border border-red-100 text-sm font-medium disabled:opacity-50"
          >
            🔥 Удалить окончательно
          </button>
        )}
        {d.actions.can_edit && (
          <button
            onClick={() => setEditOpen(true)}
            className={`w-full py-2.5 ${calmPrimaryButton}`}
          >
            ✏️ Редактировать
          </button>
        )}
        {d.actions.can_confirm_client && (
          <button
            disabled={busy === "confirm"}
            onClick={confirmClient}
            className={`w-full py-2.5 ${calmPrimaryButton}`}
          >
            ✅ Клиент подтвердил получение
          </button>
        )}
        {d.actions.can_cancel && d.status !== "cancelled" && (
          <button
            onClick={() => setCancelOpen(true)}
            className={`w-full py-2.5 ${calmPrimaryButton}`}
          >
            🚫 Отменить заявку
          </button>
        )}
        {d.actions.can_delete && (
          <button
            disabled={busy === "delete"}
            onClick={doDelete}
            className="w-full py-2.5 rounded-lg bg-red-50 text-red-700 border border-red-100 text-sm font-medium disabled:opacity-50"
          >
            🗑 Удалить из системы (админ)
          </button>
        )}
      </section>

      {editOpen && (
        <DealEditModal
          deal={d}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            onChanged();
            reload();
          }}
        />
      )}

      {editingPayment && (
        <PaymentEditModal
          payment={editingPayment}
          deal={d}
          onClose={() => setEditingPayment(null)}
          onSaved={(updated) => {
            setPayments((prev) => prev.map((p) => p.id === updated.id ? updated : p));
            setEditingPayment(null);
          }}
          onDeleted={(id) => {
            setPayments((prev) => prev.filter((p) => p.id !== id));
            setEditingPayment(null);
          }}
          canDelete={me.permissions.payments_delete}
        />
      )}

      {addingPayment && (
        <AddPaymentModal
          dealId={dealId}
          deal={d}
          onClose={() => setAddingPayment(false)}
          onCreated={(p) => { setPayments((prev) => [...prev, p]); setAddingPayment(false); }}
        />
      )}

      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black p-3">
          <div className="bg-tg-sectionBg w-full max-w-md rounded-xl p-4 space-y-3">
            <h2 className="text-base font-semibold">Отменить заявку {dealCode}</h2>
            <p className="text-xs text-tg-hint">
              Укажите короткую причину отмены — она фиксируется в системе
              вместе с этапом, на котором отменили.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Например: клиент отказался от сделки"
              className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setCancelOpen(false); setCancelReason(""); }}
                className="flex-1 py-2 rounded-lg bg-tg-secondaryBg text-sm"
              >
                Отмена
              </button>
              <button
                disabled={busy === "cancel"}
                onClick={submitCancel}
                className={`flex-1 py-2 ${calmPrimaryButton}`}
              >
                Подтвердить отмену
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Edit modal ───────────────────────

function DealEditModal({
  deal, onClose, onSaved,
}: {
  deal: DealDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Числовые поля храним как форматированные строки с разделителями
  // («1 234.50»). На submit парсим обратно. Это совпадает с тем, как
  // суммы выводятся в карточке.
  const [rate, setRate] = useState<string>(fmtMoneyInput(deal.rate));
  const [feePct, setFeePct] = useState<string>(fmtMoneyInput(deal.fee_percent));
  const [amount, setAmount] = useState<string>(fmtMoneyInput(deal.amount));
  const [fixCommAmt, setFixCommAmt] = useState<string>(
    fmtMoneyInput(deal.fixed_commission_amount),
  );
  const [fixCommCcy, setFixCommCcy] = useState<string>(
    deal.fixed_commission_currency || "",
  );
  const [instrNo, setInstrNo] = useState<string>(
    deal.instruction_no?.toString() || "",
  );
  const [cpId, setCpId] = useState<string>(deal.counterparty_id?.toString() || "");
  const [coId, setCoId] = useState<string>(deal.company_id?.toString() || "");
  const [bkId, setBkId] = useState<string>(deal.bank_id?.toString() || "");
  const [scId, setScId] = useState<string>(deal.subcontractor_id?.toString() || "");
  const [scBankId, setScBankId] = useState<string>(
    deal.subcontractor_bank_id?.toString() || "",
  );
  const [dealDate, setDealDate] = useState<string>(deal.deal_date || "");
  const [notes, setNotes] = useState<string>(deal.notes || "");

  const [dicts, setDicts] = useState<Dictionaries | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<Dictionaries>("/api/deals/_meta/dictionaries").then(setDicts).catch(
      (e) => setErr(`Не удалось загрузить справочники: ${e.message}`),
    );
  }, []);

  // Взаимоисключение «Компания vs Субподрядчик»: при выборе одного
  // другое автоматически сбрасывается (как просил клиент).
  const pickCompany = (id: string) => {
    setCoId(id);
    setBkId(""); // банк сбрасываем — будет выбран из банков новой компании
    if (id) {
      setScId("");
      setScBankId("");
    }
  };
  const pickSubcontractor = (id: string) => {
    setScId(id);
    setScBankId("");
    if (id) {
      setCoId("");
      setBkId("");
    }
  };
  const pickCompanyBank = (id: string) => {
    setBkId(id);
  };
  const pickSubBank = (id: string) => {
    setScBankId(id);
  };

  const banksOfCompany: NamedItem[] = coId && dicts
    ? dicts.banks_by_company[coId] || []
    : [];
  const banksOfSub: NamedItem[] = scId && dicts
    ? dicts.subcontractor_banks_by_sub[scId] || []
    : [];

  const submit = async () => {
    setBusy(true);
    setErr(null);
    // diff — только реально изменённые поля. Пустую строку трактуем как
    // «не менять» (а не «обнулить»), чтобы случайно не потерять значение.
    const body: Record<string, unknown> = {};
    const dec = (s: string) => {
      const t = parseMoneyInput(s);
      return t === "" ? null : Number(t);
    };
    const intv = (s: string) => (s.trim() === "" ? null : parseInt(s, 10));

    const dRate = dec(rate);
    if (dRate !== null && dRate !== deal.rate) body.rate = dRate;
    const dFee = dec(feePct);
    if (dFee !== null && dFee !== deal.fee_percent) body.fee_percent = dFee;
    const dAmt = dec(amount);
    if (dAmt !== null && dAmt !== deal.amount) body.amount = dAmt;
    const dFC = dec(fixCommAmt);
    if (dFC !== null && dFC !== deal.fixed_commission_amount) {
      body.fixed_commission_amount = dFC;
    }
    if (fixCommCcy && fixCommCcy !== (deal.fixed_commission_currency || "")) {
      body.fixed_commission_currency = fixCommCcy;
    }
    const dIn = intv(instrNo);
    if (dIn !== null && dIn !== deal.instruction_no) body.instruction_no = dIn;
    const dCp = intv(cpId);
    if (dCp !== null && dCp !== deal.counterparty_id) body.counterparty_id = dCp;
    const dCo = intv(coId);
    if (dCo !== null && dCo !== deal.company_id) body.company_id = dCo;
    const dBk = intv(bkId);
    if (dBk !== null && dBk !== deal.bank_id) body.bank_id = dBk;
    const dSc = intv(scId);
    if (dSc !== null && dSc !== deal.subcontractor_id) body.subcontractor_id = dSc;
    const dScBk = intv(scBankId);
    if (dScBk !== null && dScBk !== deal.subcontractor_bank_id) {
      body.subcontractor_bank_id = dScBk;
    }
    if (dealDate && dealDate !== deal.deal_date) body.deal_date = dealDate;
    if (notes !== (deal.notes || "")) body.notes = notes;

    if (Object.keys(body).length === 0) {
      setErr("Ничего не изменилось.");
      setBusy(false);
      return;
    }
    try {
      await api.patch(`/api/deals/${deal.id}`, body);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Helper — input для денежного поля: ввод → переформатирование с
  // разделителями. Курсор после переформатирования встаёт в конец;
  // для текущего UX это допустимо.
  const moneyHandler = (setter: (v: string) => void) => (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => setter(fmtMoneyInput(e.target.value));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black p-3 overflow-auto">
      <div className="bg-tg-sectionBg w-full max-w-md rounded-xl p-4 space-y-3 my-3">
        <h2 className="text-base font-semibold">Редактировать заявку</h2>
        <p className="text-xs text-tg-hint">
          Меняются только заполненные поля (пустое = «не трогать»). Правки
          записываются в audit-log.
        </p>
        {deal.payment_confirmed_at && (
          <div className="bg-amber-50 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 text-xs px-3 py-2 rounded-lg">
            ⚠️ Заявка уже подтверждена. Правка финансовых полей запускает
            каскад пересчёта P&L и FX-распределения.
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 text-xs">
          <label>
            Курс (rate)
            <input
              value={rate} onChange={moneyHandler(setRate)}
              inputMode="decimal"
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
          <label>
            Вознаграждение, %
            <input
              value={feePct} onChange={moneyHandler(setFeePct)}
              inputMode="decimal"
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
          <label>
            Сумма
            <input
              value={amount} onChange={moneyHandler(setAmount)}
              inputMode="decimal"
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
          <label>
            № поручения
            <input
              value={instrNo} onChange={(e) => setInstrNo(e.target.value)}
              inputMode="numeric"
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
          <label>
            Фикс-комиссия (сумма)
            <input
              value={fixCommAmt} onChange={moneyHandler(setFixCommAmt)}
              inputMode="decimal"
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
          <label>
            Фикс-комиссия (валюта)
            <input
              value={fixCommCcy} onChange={(e) => setFixCommCcy(e.target.value.toUpperCase())}
              maxLength={6}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm uppercase"
            />
          </label>

          <label className="col-span-2">
            Контрагент
            <select
              value={cpId} onChange={(e) => setCpId(e.target.value)}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            >
              <option value="">— не выбран —</option>
              {dicts?.counterparties.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>

          <label className="col-span-2">
            Исполнитель — компания{scId ? " (выбран субподрядчик)" : ""}
            <select
              value={coId}
              onChange={(e) => pickCompany(e.target.value)}
              disabled={!!scId}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm disabled:opacity-50"
            >
              <option value="">— не выбрана —</option>
              {dicts?.companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label className="col-span-2">
            Банк компании
            <select
              value={bkId}
              onChange={(e) => pickCompanyBank(e.target.value)}
              disabled={!!scId || !coId}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm disabled:opacity-50"
            >
              <option value="">— выберите банк —</option>
              {banksOfCompany.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            {coId && banksOfCompany.length === 0 && (
              <span className="text-tg-hint">У компании нет банков в справочнике.</span>
            )}
          </label>

          <label className="col-span-2">
            Исполнитель — субподрядчик{coId ? " (выбрана компания)" : ""}
            <select
              value={scId}
              onChange={(e) => pickSubcontractor(e.target.value)}
              disabled={!!coId}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm disabled:opacity-50"
            >
              <option value="">— не выбран —</option>
              {dicts?.subcontractors.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
          <label className="col-span-2">
            Банк субподрядчика
            <select
              value={scBankId}
              onChange={(e) => pickSubBank(e.target.value)}
              disabled={!!coId || !scId}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm disabled:opacity-50"
            >
              <option value="">— выберите банк —</option>
              {banksOfSub.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            {scId && banksOfSub.length === 0 && (
              <span className="text-tg-hint">У субподрядчика нет банков в справочнике.</span>
            )}
          </label>

          <div className="col-span-2 text-[11px] text-tg-hint bg-tg-bg rounded-lg px-2 py-1.5">
            ⓘ Заявку исполняет либо собственная компания, либо субподрядчик —
            одновременно их не бывает. Выбор одного автоматически снимет другой.
          </div>

          <label className="col-span-2">
            Дата заявки
            <input
              type="date" value={dealDate}
              onChange={(e) => setDealDate(e.target.value)}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
          <label className="col-span-2">
            Комментарий
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
        </div>

        {err && <div className="text-xs text-red-500">{err}</div>}

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-lg bg-tg-secondaryBg text-sm"
          >
            Отмена
          </button>
          <button
            disabled={busy}
            onClick={submit}
            className={`flex-1 py-2 ${calmPrimaryButton}`}
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}
